import React, { useMemo } from 'react';

/**
 * LoginBackground — Grid geométrico com nós em interseções reais.
 * 
 * Grid matemático: células de 80px.
 * Nós posicionados exclusivamente em interseções do grid.
 * Conexões lógicas entre nós adjacentes (Delaunay-like).
 * Densidade adaptativa: menos nós em mobile.
 * Usa exclusivamente token --brand com opacidade 5%.
 */

interface GridNode {
  x: number;
  y: number;
  r: number; // radius
}

interface GridConnection {
  from: number;
  to: number;
}

// Grid-aligned nodes at real intersection points (based on 80px grid)
// Coordinates in absolute px, viewBox handles scaling
const CELL = 80;
const GRID_W = 20; // 20 cells = 1600px
const GRID_H = 12; // 12 cells = 960px
const VB_W = GRID_W * CELL; // 1600
const VB_H = GRID_H * CELL; // 960

// Nodes placed at exact grid intersections — spread across entire viewport
const ALL_NODES: GridNode[] = [
  // Top region
  { x: 2 * CELL, y: 1 * CELL, r: 4 },
  { x: 5 * CELL, y: 2 * CELL, r: 5 },
  { x: 8 * CELL, y: 1 * CELL, r: 4 },
  { x: 12 * CELL, y: 2 * CELL, r: 4.5 },
  { x: 15 * CELL, y: 1 * CELL, r: 4 },
  { x: 18 * CELL, y: 2 * CELL, r: 5 },
  // Upper-mid
  { x: 1 * CELL, y: 4 * CELL, r: 4.5 },
  { x: 4 * CELL, y: 4 * CELL, r: 4 },
  { x: 7 * CELL, y: 3 * CELL, r: 5 },
  { x: 10 * CELL, y: 4 * CELL, r: 4 },
  { x: 14 * CELL, y: 4 * CELL, r: 4.5 },
  { x: 17 * CELL, y: 3 * CELL, r: 4 },
  { x: 19 * CELL, y: 4 * CELL, r: 4 },
  // Center (sparser — login card zone)
  { x: 3 * CELL, y: 6 * CELL, r: 3.5 },
  { x: 16 * CELL, y: 6 * CELL, r: 3.5 },
  // Lower-mid
  { x: 1 * CELL, y: 8 * CELL, r: 4 },
  { x: 4 * CELL, y: 8 * CELL, r: 5 },
  { x: 7 * CELL, y: 9 * CELL, r: 4 },
  { x: 11 * CELL, y: 8 * CELL, r: 4.5 },
  { x: 14 * CELL, y: 9 * CELL, r: 4 },
  { x: 17 * CELL, y: 8 * CELL, r: 5 },
  { x: 19 * CELL, y: 9 * CELL, r: 4 },
  // Bottom region
  { x: 2 * CELL, y: 11 * CELL, r: 4 },
  { x: 6 * CELL, y: 11 * CELL, r: 4.5 },
  { x: 10 * CELL, y: 11 * CELL, r: 4 },
  { x: 13 * CELL, y: 10 * CELL, r: 5 },
  { x: 16 * CELL, y: 11 * CELL, r: 4 },
  { x: 18 * CELL, y: 10 * CELL, r: 4.5 },
];

// Logical connections between adjacent nodes (by index)
const CONNECTIONS: GridConnection[] = [
  // Top cluster
  { from: 0, to: 1 }, { from: 1, to: 2 }, { from: 2, to: 3 },
  { from: 3, to: 4 }, { from: 4, to: 5 },
  // Top to upper-mid verticals
  { from: 0, to: 6 }, { from: 0, to: 7 }, { from: 1, to: 7 },
  { from: 1, to: 8 }, { from: 2, to: 8 }, { from: 3, to: 9 },
  { from: 3, to: 10 }, { from: 4, to: 11 }, { from: 5, to: 12 },
  // Upper-mid horizontals
  { from: 6, to: 7 }, { from: 7, to: 8 }, { from: 8, to: 9 },
  { from: 9, to: 10 }, { from: 10, to: 11 }, { from: 11, to: 12 },
  // Upper-mid to center sparse
  { from: 7, to: 13 }, { from: 6, to: 13 },
  { from: 12, to: 14 }, { from: 10, to: 14 },
  // Center to lower-mid
  { from: 13, to: 15 }, { from: 13, to: 16 },
  { from: 14, to: 20 }, { from: 14, to: 21 },
  // Lower-mid horizontals
  { from: 15, to: 16 }, { from: 16, to: 17 }, { from: 17, to: 18 },
  { from: 18, to: 19 }, { from: 19, to: 20 }, { from: 20, to: 21 },
  // Lower-mid to bottom
  { from: 15, to: 22 }, { from: 16, to: 22 }, { from: 16, to: 23 },
  { from: 17, to: 23 }, { from: 18, to: 24 }, { from: 18, to: 25 },
  { from: 19, to: 25 }, { from: 20, to: 26 }, { from: 21, to: 27 },
  // Bottom horizontals
  { from: 22, to: 23 }, { from: 23, to: 24 }, { from: 24, to: 25 },
  { from: 25, to: 26 }, { from: 26, to: 27 },
];

// Mobile-visible nodes (subset — reduced density)
const MOBILE_NODE_INDICES = new Set([
  0, 2, 4, 6, 8, 10, 13, 14, 15, 17, 19, 22, 24, 26
]);

const MOBILE_CONNECTIONS = CONNECTIONS.filter(
  c => MOBILE_NODE_INDICES.has(c.from) && MOBILE_NODE_INDICES.has(c.to)
);

const LoginBackground: React.FC = () => {
  const gridColor = 'hsl(var(--brand) / 0.09)';
  const connectionColor = 'hsl(var(--brand) / 0.11)';
  const nodeColor = 'hsl(var(--brand) / 0.13)';
  const strokeW = 0.9;
  const nodeR = 5;

  return (
    <>
      {/* Desktop grid — hidden on small screens */}
      <svg
        className="absolute inset-0 w-full h-full pointer-events-none hidden md:block"
        viewBox={`0 0 ${VB_W} ${VB_H}`}
        preserveAspectRatio="xMidYMid slice"
        xmlns="http://www.w3.org/2000/svg"
        aria-hidden="true"
      >
        <defs>
          <pattern id="login-grid-d" width={CELL} height={CELL} patternUnits="userSpaceOnUse">
            <path
              d={`M ${CELL} 0 L 0 0 0 ${CELL}`}
              fill="none"
              stroke={gridColor}
              strokeWidth="0.5"
            />
          </pattern>
        </defs>
        <rect width="100%" height="100%" fill="url(#login-grid-d)" />
        <g>
          {CONNECTIONS.map((c, i) => (
            <line
              key={`c${i}`}
              x1={ALL_NODES[c.from].x}
              y1={ALL_NODES[c.from].y}
              x2={ALL_NODES[c.to].x}
              y2={ALL_NODES[c.to].y}
              stroke={connectionColor}
              strokeWidth={strokeW}
            />
          ))}
          {ALL_NODES.map((n, i) => (
            <circle key={`n${i}`} cx={n.x} cy={n.y} r={nodeR} fill={nodeColor} stroke="none" />
          ))}
        </g>
      </svg>

      {/* Mobile grid — reduced density, visible only on small screens */}
      <svg
        className="absolute inset-0 w-full h-full pointer-events-none md:hidden"
        viewBox={`0 0 ${VB_W} ${VB_H}`}
        preserveAspectRatio="xMidYMid slice"
        xmlns="http://www.w3.org/2000/svg"
        aria-hidden="true"
      >
        <defs>
          <pattern id="login-grid-m" width={CELL * 2} height={CELL * 2} patternUnits="userSpaceOnUse">
            <path
              d={`M ${CELL * 2} 0 L 0 0 0 ${CELL * 2}`}
              fill="none"
              stroke={gridColor}
              strokeWidth="0.5"
            />
          </pattern>
        </defs>
        <rect width="100%" height="100%" fill="url(#login-grid-m)" />
        <g>
          {MOBILE_CONNECTIONS.map((c, i) => (
            <line
              key={`mc${i}`}
              x1={ALL_NODES[c.from].x}
              y1={ALL_NODES[c.from].y}
              x2={ALL_NODES[c.to].x}
              y2={ALL_NODES[c.to].y}
              stroke={connectionColor}
              strokeWidth={strokeW}
            />
          ))}
          {ALL_NODES.filter((_, i) => MOBILE_NODE_INDICES.has(i)).map((n, i) => (
            <circle key={`mn${i}`} cx={n.x} cy={n.y} r={nodeR} fill={nodeColor} stroke="none" />
          ))}
        </g>
      </svg>
    </>
  );
};

export default LoginBackground;
